import { Info, Zap, Users, Shield, Globe, Award } from 'lucide-react';
import { SEO } from '../components/SEO';

export default function ONama() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <SEO 
        title="O nama - Online Kalkulator"
        description="Saznajte više o Online Kalkulatoru - vašem pouzdanom alatu za brze i točne izračune. Otkrijte kako funkcionira i zašto ga koriste tisuće korisnika."
        keywords="o nama, online kalkulator, o kalkulatoru, kako funkcionira"
      />
      
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Info className="w-12 h-12 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-4">O nama</h1>
          <p className="text-xl text-gray-600">Vaš pouzdani online alat za brze i točne izračune</p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Što je Online Kalkulator?</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Online Kalkulator je besplatna web aplikacija koja vam nudi pristup više od 100 različitih kalkulatora 
            za svakodnevne zadatke. Od osnovnih matematičkih operacija do specijaliziranih izračuna, 
            naša platforma je dizajnirana da bude jednostavna, brza i pouzdana.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Bez potrebe za instalacijom ili registracijom, jednostavno otvorite stranicu i počnite koristiti 
            kalkulatore koji vam trebaju. Sve je dostupno 24/7, besplatno i bez skrivenih naknada.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Feature 1 */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center mb-4">
              <Zap className="w-8 h-8 text-yellow-500 mr-3" />
              <h3 className="text-xl font-bold text-gray-800">Brzo i Jednostavno</h3>
            </div>
            <p className="text-gray-600">
              Intuitivni sučelje omogućava vam da dobijete rezultate u samo nekoliko klikova. 
              Nema komplikacija, samo brzi i točni izračuni.
            </p>
          </div>

          {/* Feature 2 */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center mb-4">
              <Shield className="w-8 h-8 text-green-500 mr-3" />
              <h3 className="text-xl font-bold text-gray-800">Sigurno i Privatno</h3>
            </div>
            <p className="text-gray-600">
              Svi izračuni se obavljaju u vašem pregledniku. Vaši podaci se nikada ne prate niti se šalju 
              na naše servere. Potpuna privatnost i sigurnost.
            </p>
          </div>

          {/* Feature 3 */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center mb-4">
              <Globe className="w-8 h-8 text-blue-500 mr-3" />
              <h3 className="text-xl font-bold text-gray-800">Dostupno Svugdje</h3>
            </div>
            <p className="text-gray-600">
              Koristite Online Kalkulator na bilo kojem uređaju - računalu, tabletu ili mobilnom telefonu. 
              Bez instalacije, samo otvorite preglednik.
            </p>
          </div>

          {/* Feature 4 */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center mb-4">
              <Award className="w-8 h-8 text-purple-500 mr-3" />
              <h3 className="text-xl font-bold text-gray-800">Pouzdano i Točno</h3>
            </div>
            <p className="text-gray-600">
              Svi kalkulatori koriste matematički provjerene formule. Možete biti sigurni da su rezultati 
              točni i pouzdani.
            </p>
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Kako Funkcionira?</h2>
          
          <div className="space-y-6">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-10 w-10 rounded-md bg-blue-600 text-white font-bold">
                  1
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Odaberite Kalkulator</h3>
                <p className="text-gray-600">
                  Pregledajte našu kolekciju od preko 100 kalkulatora i odaberite onaj koji vam trebate. 
                  Kalkulatori su organizirani po kategorijama za lakšu pretragu.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-10 w-10 rounded-md bg-blue-600 text-white font-bold">
                  2
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Unesite Podatke</h3>
                <p className="text-gray-600">
                  Unesite potrebne vrijednosti u polja. Svaki kalkulator ima jasne upute što trebate unijeti 
                  i koja je jedinica mjere.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-10 w-10 rounded-md bg-blue-600 text-white font-bold">
                  3
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Dobijte Rezultat</h3>
                <p className="text-gray-600">
                  Rezultat se prikazuje trenutno, bez potrebe za osvježavanjem stranice. Možete lako 
                  isprobati različite vrijednosti i vidjeti kako se rezultat mijenja.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-10 w-10 rounded-md bg-blue-600 text-white font-bold">
                  4
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Kopirajte ili Podijelite</h3>
                <p className="text-gray-600">
                  Lako kopirajte rezultat ili podijelite stranicu sa prijateljima. Sve je jednostavno 
                  i brzo.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Available Calculators */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Dostupni Kalkulatori</h2>
          <p className="text-gray-700 mb-4">
            Online Kalkulator nudi širok spektar kalkulatora organiziranih u sljedeće kategorije:
          </p>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-gray-600">
            <li className="flex items-center"><span className="text-blue-600 mr-2">✓</span> Matematički kalkulatori</li>
            <li className="flex items-center"><span className="text-blue-600 mr-2">✓</span> Financijski kalkulatori</li>
            <li className="flex items-center"><span className="text-blue-600 mr-2">✓</span> Zdravstveni kalkulatori</li>
            <li className="flex items-center"><span className="text-blue-600 mr-2">✓</span> Konverteri jedinica</li>
            <li className="flex items-center"><span className="text-blue-600 mr-2">✓</span> Građevinski kalkulatori</li>
            <li className="flex items-center"><span className="text-blue-600 mr-2">✓</span> Automobilski kalkulatori</li>
            <li className="flex items-center"><span className="text-blue-600 mr-2">✓</span> Vremenske kalkulacije</li>
            <li className="flex items-center"><span className="text-blue-600 mr-2">✓</span> I mnogo više...</li>
          </ul>
        </div>

        {/* Why Choose Us */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg shadow-lg p-8 text-white">
          <div className="flex items-center mb-4">
            <Users className="w-8 h-8 mr-3" />
            <h2 className="text-2xl font-bold">Zašto Odabrati Online Kalkulator?</h2>
          </div>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-yellow-300 mr-3 font-bold">→</span>
              <span><strong>Besplatno:</strong> Svi kalkulatori su potpuno besplatni, bez skrivenih naknada</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-300 mr-3 font-bold">→</span>
              <span><strong>Bez Registracije:</strong> Nije potrebna registracija ili prijava</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-300 mr-3 font-bold">→</span>
              <span><strong>Brz i Jednostavan:</strong> Intuitivno sučelje za sve dobi</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-300 mr-3 font-bold">→</span>
              <span><strong>Pouzdano:</strong> Matematički provjereni algoritmi</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-300 mr-3 font-bold">→</span>
              <span><strong>Dostupno 24/7:</strong> Koristi kalkulatore kada god ti trebaju</span>
            </li>
          </ul>
        </div>

        {/* Contact Section */}
        <div className="text-center mt-12">
          <p className="text-gray-600 mb-4">
            Imate pitanja ili prijedloge? Slobodno nam se obratite!
          </p>
          <a 
            href="mailto:info@online-kalkulator.com" 
            className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Kontaktirajte nas
          </a>
        </div>
      </div>
    </div>
  );
}
